import { SnapshotAssembler, decodeState, routedUrl, withQuery, type Mode, type PlayerInfo, type ServerMessage } from '@orb/protocol';
import type { Action, Sim, SimEvent, SimState } from '@orb/sim';
import { hostEnv, type Env } from './env.ts';
import { Link, type LinkState } from './link.ts';
import { Lockstep } from './lockstep.ts';

export interface NetGameOptions {
  url: string;
  /** Join this room, wait in the random-match queue for `queue` mode, or create a room of `create` mode. */
  join?: string;
  queue?: Mode;
  create?: Mode;
  /** A session token from before (a reload or a dropped connection): the server puts us back in our seat. */
  session?: string | null;
  /** The account's session token, so the match counts towards its progress. */
  auth?: string | null;
  env?: Env;
}
export interface RoomInfo { code: string; seed: number; mode: Mode; seat: number; startTick: number; players: PlayerInfo[] }

/** More ticks than this in one step are a catch-up: played without effects, then the views resync. */
const QUIET_ABOVE = 2;

/**
 * A networked match from the client's side: gets a seat (or gets its old one back), then feeds the
 * simulation exactly the turns the server sends. Rendering-free, so browsers and headless test
 * clients share it.
 */
export class NetGame {
  readonly link: Link;
  lockstep: Lockstep | null = null;
  room: RoomInfo | null = null;
  readonly errors: number[] = [];
  readonly desyncs: number[] = [];
  /** Times the server put us back into the match (after a drop or a reload). */
  resumes = 0;
  /** The server's verdicts on our result claims. */
  readonly verdicts: Extract<ServerMessage, { type: 'verdict' }>[] = [];
  /**
   * The match is gone for us: reconnected, but the server no longer holds our seat (it restarted for
   * a deploy — the match was recorded — or we were away longer than the resume window).
   */
  lost = false;
  onLost: () => void = () => {};
  onMessage: (m: ServerMessage) => void = () => {};
  onState: (s: LinkState) => void = () => {};
  private readonly env: Env;
  private opts: NetGameOptions;
  private adaptedAt = 0;
  private readonly assembler = new SnapshotAssembler();
  /** A received state (the server's replay) to jump to on the next step. */
  private restore: { tick: number; state: SimState } | null = null;

  constructor(opts: NetGameOptions) {
    this.opts = opts;
    this.env = opts.env ?? hostEnv();
    // a room we know lives on its instance; a random match starts at the matchmaker (`q`)
    const url = opts.join ? routedUrl(opts.url, opts.join) : opts.queue ? withQuery(opts.url, { q: '1' }) : opts.url;
    this.link = new Link({ url, session: opts.session, auth: opts.auth, env: this.env });
    this.link.onState = (s) => this.onState(s);
  }

  /** Connect and take a seat; resolves with the room, rejects on a server error before that. */
  start(): Promise<RoomInfo> {
    return new Promise((resolve, reject) => {
      this.link.onOpen = (resumed) => {
        if (this.room && !resumed && !this.lost) { this.lost = true; this.lockstep = null; this.onLost(); }
        if (resumed || this.room) return; // the server sends `room` for a resumed seat by itself
        if (this.opts.join) this.link.send({ type: 'join_room', code: this.opts.join });
        else if (this.opts.queue) this.link.send({ type: 'queue', mode: this.opts.queue });
        else this.link.send({ type: 'create_room', mode: this.opts.create ?? 'coop' });
      };
      this.link.onMessage = (m) => {
        if (m.type === 'room') this.seated(m, resolve);
        else if (m.type === 'match') this.moveTo(m.code);
        else if (m.type === 'turn') this.lockstep?.receive(m);
        else if (m.type === 'snapshot') this.snapshotPart(m);
        else if (m.type === 'verdict') this.verdicts.push(m);
        else if (m.type === 'error') { this.errors.push(m.code); if (!this.room) reject(new Error(`server error ${m.code}`)); }
        else if (m.type === 'desync') this.desyncs.push(m.tick);
        this.onMessage(m);
      };
      this.link.connect();
    });
  }

  /**
   * `room` arrives when we take a seat, and again when the server gives our seat back. A fresh match
   * starts at tick 0; otherwise we say what we already have (`resync`) and the server sends the rest.
   */
  private seated(m: Extract<ServerMessage, { type: 'room' }>, resolve: (r: RoomInfo) => void) {
    const first = !this.room;
    // reconnects must reach this room's instance (a created room's code tells which)
    if (first) this.link.retarget(routedUrl(this.opts.url, m.code));
    this.room = { code: m.code, seed: m.seed, mode: m.mode, seat: m.seat, startTick: m.startTick, players: m.players };
    if (first) {
      // a reload starts from the seed at tick 0 and replays (or is given a snapshot)
      this.lockstep = new Lockstep(this.link, 0);
      if (m.startTick > 0) { this.resumes++; this.link.send({ type: 'resync', fromTick: 0 }); }
      resolve(this.room);
      return;
    }
    // same page, connection back: we hold every turn before `received`
    this.resumes++;
    this.lockstep!.gap = false;
    this.link.send({ type: 'resync', fromTick: this.lockstep!.received });
  }

  /** The random match's room is on another instance: go there and join it. */
  private moveTo(code: string) {
    if (this.room) return;
    this.opts = { ...this.opts, join: code, queue: undefined };
    this.link.redial(routedUrl(this.opts.url, code));
  }

  private snapshotPart(m: Extract<ServerMessage, { type: 'snapshot' }>) {
    try {
      const whole = this.assembler.add(m);
      if (whole) this.restore = { tick: m.tick, state: decodeState(whole) };
    } catch { this.assembler.reset(); }
  }

  get seat() { return this.room?.seat ?? 0; }

  /** Queue an own action; it takes effect `delay` ticks later, when the server's turn brings it back. */
  send(action: Action, sim: Sim) { this.lockstep?.send(action, sim.tick); }

  /**
   * One 60 Hz loop step: run as many sim ticks as the received turns allow (0, 1, 2 when catching up,
   * up to hundreds when fast-forwarding), reporting checksums on the way.
   * A fast-forward runs quietly — no per-tick events — and ends with `onResync` so the views can
   * rebuild from the state; so does a restored snapshot. Returns how many ticks ran.
   */
  advance(sim: Sim, onTick: (events: readonly SimEvent[]) => void, onResync: () => void = () => {}): number {
    const ls = this.lockstep; if (!ls) return 0;
    let resync = false;
    if (this.restore) {
      const r = this.restore; this.restore = null;
      if (ls.canRestore(r.tick, sim.tick)) { sim.restore(r.state); ls.restoreAt(r.tick); resync = true; }
    }
    const now = this.env.now();
    if (now - this.adaptedAt >= 1000) { this.adaptedAt = now; ls.adapt(this.link.rtt, this.link.rttVar); }
    const n = ls.ticksThisStep(sim.tick, this.link.rttVar), quiet = n > QUIET_ABOVE;
    for (let k = 0; k < n; k++) {
      const events = sim.step(ls.take(sim.tick));
      ls.stepped(sim.tick, () => sim.checksum());
      if (!quiet) onTick(events);
    }
    if (quiet || resync) onResync();
    return n;
  }

  /**
   * Claim the result as it stands: our state's hash and tally at the current tick. The server replays
   * its log and answers with a `verdict` (kept in `verdicts`); only its own numbers ever count.
   */
  claim(sim: Sim) {
    this.link.send({ type: 'result', tick: sim.tick, hash: sim.checksum(), kills: [...sim.state.stats.kills] });
  }

  /** Stop waiting in the queue (the server drops the ticket), then disconnect. */
  cancel() { if (!this.room) this.link.send({ type: 'leave' }); this.close(); }

  /**
   * Leave on purpose: the server frees the seat (the ally hears «left», not «lost connection») and
   * forgets the session, so the next room or invite isn't swallowed by a resume. Then disconnect.
   */
  leave() { this.link.send({ type: 'leave' }); this.close(); }

  close() { this.link.close(); }
}
